import React, { useCallback } from 'react';

import type { PopoverProps } from '@mui/material';
import { AlineTooltip } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { WorkflowRecipientType } from 'src/gql/generated/apollo-types';
import { useAddWorkflowPermissions, useCreateUserDefinedRole } from 'src/hooks';
import type { WorkflowStep } from 'src/workflow';

import type { CollaboratorAutocompleteValue } from './WorkflowCollaboratorsAutocomplete';
import type {
  WorkflowAssignmentAutocompleteProps,
  WorkflowAssignmentOption,
  WorkflowCollaboratorOption,
} from '../WorkflowAssignmentAutocomplete';
import {
  toWorkflowCollaboratorOption,
  WorkflowAssignmentAutocomplete,
  WorkflowAssignmentAutocompleteContext,
} from '../WorkflowAssignmentAutocomplete';

interface WorkflowCollaboratorsChooserProps extends Pick<
  WorkflowAssignmentAutocompleteProps,
  'isDelegatedToAi' | 'onChange' | 'placeholder'
> {
  value: CollaboratorAutocompleteValue | null;
  workflowId: UUID;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  autoFocus?: boolean;
  context?: WorkflowAssignmentAutocompleteContext;
  dataCy?: string;
  disabledReason?: string;
  emailOnly?: boolean;
}

export function WorkflowCollaboratorsChooser({
  autoFocus,
  context = WorkflowAssignmentAutocompleteContext.WorkflowCollaborators,
  dataCy,
  disabledReason,
  emailOnly,
  isDelegatedToAi,
  onChange,
  placeholder,
  value,
  workflowId,
}: WorkflowCollaboratorsChooserProps): JSX.Element | null {
  return (
    <>
      <AlineTooltip arrow placement="top" title={disabledReason ?? ''}>
        <div>
          <WorkflowAssignmentAutocomplete
            autoFocus={autoFocus}
            context={context}
            dataCy={dataCy}
            disabled={Boolean(disabledReason)}
            emailOnly={emailOnly}
            isDelegatedToAi={isDelegatedToAi}
            placeholder={placeholder}
            value={value ? toWorkflowCollaboratorOption(value) : null}
            workflowId={workflowId}
            onChange={onChange}
          />
        </div>
      </AlineTooltip>
    </>
  );
}

export function getWorkflowCollaboratorOptionDisabled({
  context,
  option,
  steps,
}: {
  context: WorkflowAssignmentAutocompleteContext;
  option: WorkflowAssignmentOption;
  steps: WorkflowStep[];
}): boolean {
  if (!steps.length || context === WorkflowAssignmentAutocompleteContext.WorkflowTask) return false;

  if (option.__typename === 'NewCollaborator' || option.__typename === 'WorkflowLauncher')
    return false;

  const userActions = steps.flatMap(step =>
    step.actions
      .filter(action => {
        switch (option.__typename) {
          case 'WorkflowPermission':
            if (option.isLauncher) return action.permission.id === option.id;
            return action.assignee?.id === option.account?.id;
          case 'UserDefinedRole':
            return action.assignee?.role.toLowerCase() === option.roleName.toLowerCase();
          case 'User':
            return action.assignee?.id === option.id;
        }
      })
      .map(action => action.type)
  );

  return (
    userActions.includes(WorkflowRecipientType.Signer) &&
    userActions.includes(WorkflowRecipientType.CarbonCopy)
  );
}

export function useCreateRoleHandler() {
  const { createRole } = useCreateUserDefinedRole();
  const createRoleAndGetId = useCallback(
    async (roleName: string) => {
      const { data } = await createRole({ variables: { input: { props: { roleName } } } });
      return data?.createUserDefinedRole?.userDefinedRole.id ?? null;
    },
    [createRole]
  );

  return { createRoleAndGetId };
}

export function useHandleAddCollaborator(workflowId: UUID) {
  const { createRoleAndGetId } = useCreateRoleHandler();
  const { addWorkflowPermissions } = useAddWorkflowPermissions();
  const handleAddCollaborator = useCallback(
    async (option: Exclude<WorkflowAssignmentOption, WorkflowCollaboratorOption>) => {
      switch (option.__typename) {
        /**
         * Aline AI is assigned by the consumer (TaskAssigneePicker), never added as
         * a workflow permission here.
         */
        case 'AlineAi':
          return null;
        case 'NewCollaborator': {
          if (option.type === 'email') {
            const { data } = await addWorkflowPermissions(workflowId, {
              variables: {
                input: {
                  props: [
                    {
                      email: option.text,
                      timeliness: Apollo.WorkflowPermissionTimeliness.Queued,
                    },
                  ],
                  workflowId,
                },
              },
            });

            return data?.addWorkflowPermissions.at(0) ?? null;
          }

          const roleId = await createRoleAndGetId(option.text);
          if (!roleId) return null;

          const { data } = await addWorkflowPermissions(workflowId, {
            variables: {
              input: {
                props: [
                  {
                    roleId,
                    timeliness: Apollo.WorkflowPermissionTimeliness.Queued,
                  },
                ],
                workflowId,
              },
            },
          });
          return data?.addWorkflowPermissions.at(0) ?? null;
        }
        case 'UserDefinedRole': {
          const { data } = await addWorkflowPermissions(workflowId, {
            variables: {
              input: {
                props: [
                  {
                    roleId: option.id,
                    timeliness: Apollo.WorkflowPermissionTimeliness.Queued,
                  },
                ],
                workflowId,
              },
            },
          });
          return data?.addWorkflowPermissions.at(0) ?? null;
        }
        case 'User': {
          const { data } = await addWorkflowPermissions(workflowId, {
            variables: {
              input: {
                props: [
                  {
                    timeliness: Apollo.WorkflowPermissionTimeliness.Queued,
                    userId: option.id,
                  },
                ],
                workflowId,
              },
            },
          });
          return data?.addWorkflowPermissions.at(0) ?? null;
        }
      }
    },
    [addWorkflowPermissions, createRoleAndGetId, workflowId]
  );

  return { handleAddCollaborator };
}
